import { useEffect, useMemo, useState } from "react";
import {
  useGetWarehouseQuery,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
} from "../../../../redux/api";
import type { Warehouse as WarehouseModel } from "../../../../redux/api";
import { validateEmail, validatePhone } from "../../../../utils/validation";
import { PhoneInput } from "../../../../components/PhoneInput";
import { Modal } from "../../../../components/Modal";
import { EntityMeta } from "../../../../components/EntityMeta";
import { Field } from "../../../../components/Field";
import { statusTone } from "../../../../utils/status";

interface WarehouseFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
}

type WarehouseForm = {
  name: string;
  code: string;
  type: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  contactPerson: string;
  phone: string;
  email: string;
  capacity: string;
  status: string;
};

const emptyForm: WarehouseForm = {
  name: "",
  code: "",
  type: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "India",
  postalCode: "",
  contactPerson: "",
  phone: "",
  email: "",
  capacity: "",
  status: "active",
};

const formFromWarehouse = (w: WarehouseModel): WarehouseForm => ({
  name: w.name ?? "",
  code: w.code ?? "",
  type: w.type ?? "",
  addressLine1: w.addressLine1 ?? "",
  addressLine2: w.addressLine2 ?? "",
  city: w.city ?? "",
  state: w.state ?? "",
  country: w.country ?? "India",
  postalCode: w.postalCode ?? "",
  contactPerson: w.contactPerson ?? "",
  phone: w.phone ?? "",
  email: w.email ?? "",
  capacity: w.capacity != null ? String(w.capacity) : "",
  status: w.status ?? "active",
});

const num = (value: string): number | undefined =>
  value.trim() === "" ? undefined : Number(value);

// Inventory → Warehouse add/edit popup.
export const WarehouseFormModal = ({ open, id, onClose }: WarehouseFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: existing } = useGetWarehouseQuery(activeId ?? "", {
    skip: !activeId,
  });

  const [createWarehouse, { isLoading: creating }] = useCreateWarehouseMutation();
  const [updateWarehouse, { isLoading: updating }] = useUpdateWarehouseMutation();

  const [form, setForm] = useState<WarehouseForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  // Re-sync to the record being opened every time the popup opens (or the
  // target id changes while open) — this component stays mounted between
  // opens, so state from a previous visit would otherwise linger.
  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (id && existing && existing.id === id) {
      setForm(formFromWarehouse(existing));
    } else if (!id) {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  useEffect(() => {
    if (existing) setForm(formFromWarehouse(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof WarehouseForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const baseline = useMemo(
    () => (existing ? formFromWarehouse(existing) : emptyForm),
    [existing],
  );
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = "Name is required.";
    if (!form.code.trim()) errors.code = "Code is required.";
    const emailError = validateEmail(form.email, false);
    if (emailError) errors.email = emailError;
    const phoneError = validatePhone(form.phone);
    if (phoneError) errors.phone = phoneError;

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      type: form.type.trim() || undefined,
      addressLine1: form.addressLine1.trim() || undefined,
      addressLine2: form.addressLine2.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      country: form.country.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      contactPerson: form.contactPerson.trim() || undefined,
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      capacity: num(form.capacity),
      status: form.status,
    };

    try {
      if (isEdit && activeId) {
        await updateWarehouse({ id: activeId, input: payload }).unwrap();
      } else {
        await createWarehouse({ ...payload }).unwrap();
      }
      onClose();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save warehouse.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit warehouse" : "New warehouse"}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || (isEdit && !dirty)}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create warehouse"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: form.status, tone: statusTone(form.status) } : undefined}
      />

      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <Field label="Name *" error={fieldErrors.name}>
          <input
            value={form.name}
            onChange={(e) => {
              setField("name", e.target.value);
              setFieldErrors((prev) => (prev.name ? { ...prev, name: undefined } : prev));
            }}
          />
        </Field>
        <Field label="Code *" error={fieldErrors.code}>
          <input
            value={form.code}
            onChange={(e) => {
              setField("code", e.target.value);
              setFieldErrors((prev) => (prev.code ? { ...prev, code: undefined } : prev));
            }}
            placeholder="WH-001"
          />
        </Field>
        <label className="field">
          Type
          <input
            value={form.type}
            onChange={(e) => setField("type", e.target.value)}
            placeholder="Main, Cold storage, …"
          />
        </label>
        <label className="field">
          Capacity
          <input
            type="number"
            value={form.capacity}
            onChange={(e) => setField("capacity", e.target.value)}
          />
        </label>
        <label className="field">
          Contact person
          <input
            value={form.contactPerson}
            onChange={(e) => setField("contactPerson", e.target.value)}
          />
        </label>
        <Field label="Email" error={fieldErrors.email}>
          <input
            type="email"
            value={form.email}
            onChange={(e) => {
              setField("email", e.target.value);
              setFieldErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
            }}
          />
        </Field>
        <Field label="Phone" error={fieldErrors.phone}>
          <PhoneInput
            value={form.phone}
            onChange={(v) => {
              setField("phone", v);
              setFieldErrors((prev) => (prev.phone ? { ...prev, phone: undefined } : prev));
            }}
          />
        </Field>
        <label className="field field--full">
          Address line 1
          <input
            value={form.addressLine1}
            onChange={(e) => setField("addressLine1", e.target.value)}
          />
        </label>
        <label className="field field--full">
          Address line 2
          <input
            value={form.addressLine2}
            onChange={(e) => setField("addressLine2", e.target.value)}
          />
        </label>
        <label className="field">
          City
          <input
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
          />
        </label>
        <label className="field">
          State
          <input
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
          />
        </label>
        <label className="field">
          Country
          <input
            value={form.country}
            onChange={(e) => setField("country", e.target.value)}
          />
        </label>
        <label className="field">
          Postal code
          <input
            value={form.postalCode}
            onChange={(e) => setField("postalCode", e.target.value)}
          />
        </label>
        <label className="field">
          Status
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>
    </Modal>
  );
};
